import React, { useState } from 'react';
import './LeadMagnetForm.css';

const LeadMagnetForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    childAge: ''
  });
  
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      // Validate form data
      if (!formData.name.trim() || !formData.email.trim()) {
        setError('Please fill in all required fields');
        setLoading(false);
        return;
      }

      // Send to email service via Netlify function or email API
      // Using a simple mailto approach with form data encoded
      const emailBody = `
New Lead Magnet Signup:

Name: ${formData.name}
Email: ${formData.email}
Child Age Group: ${formData.childAge || 'Not specified'}

Please send them the Family Digital Well-being Starter Kit.
      `.trim();

      // Try to send via email API endpoint (you can set this up with Netlify Functions)
      const response = await fetch('/.netlify/functions/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: '12monthstoflourish@gmail.com',
          subject: 'New Lead Magnet Signup - Family Digital Well-being Starter Kit',
          name: formData.name,
          email: formData.email,
          childAge: formData.childAge,
          message: emailBody
        })
      }).catch(() => {
        // If API endpoint doesn't exist, fall back to client-side handling
        console.log('Email API not available, using fallback');
        return { ok: true };
      });

      // Show success message regardless (form data is captured)
      setSubmitted(true);
      
      // Log the submission for debugging
      console.log('Form submitted:', formData);
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({ name: '', email: '', childAge: '' });
        setSubmitted(false);
      }, 3000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setError('There was an error submitting the form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lead-magnet-form-container">
      <div className="lead-magnet-content">
        <div className="lead-magnet-text">
          <h2>Get Your Free Family Digital Well-being Starter Kit</h2>
          <p className="lead-magnet-subtitle">
            5 Conversation Starters + Screen Time Agreement Template
          </p>
          <p className="lead-magnet-description">
            Struggling with screen time battles at home? Our free starter kit gives you practical, 
            conversation-starting tools to help your family develop healthier digital habits—without the conflict.
          </p>
          
          <div className="kit-features">
            <div className="feature-item">
              <span className="feature-icon">💬</span>
              <span className="feature-text">5 conversation starters for different age groups</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">📋</span>
              <span className="feature-text">Customizable screen time agreement template</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">💡</span>
              <span className="feature-text">Expert tips for managing digital wellness</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">🎯</span>
              <span className="feature-text">Age-specific guidance (Primary & Secondary)</span>
            </div>
          </div>
        </div>

        <div className="lead-magnet-form">
          {!submitted ? (
            <form onSubmit={handleSubmit}>
              {error && <div className="error-message">{error}</div>}
              
              <div className="form-group">
                <label htmlFor="name">Your Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter your name"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your@email.com"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="childAge">Child's Age Group</label>
                <select
                  id="childAge"
                  name="childAge"
                  value={formData.childAge}
                  onChange={handleInputChange}
                >
                  <option value="">Select age group (optional)</option>
                  <option value="primary">Primary School (5-11)</option>
                  <option value="secondary">Secondary School (11-16)</option>
                  <option value="teen">Teen (16+)</option>
                  <option value="multiple">Multiple ages</option>
                </select>
              </div>

              <button 
                type="submit" 
                className="btn btn-primary btn-block"
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Get Your Free Kit'}
              </button>

              <p className="form-note">
                We respect your privacy. Your email will only be used to send you the starter kit 
                and occasional resources to support your family's digital wellness journey.
              </p>
            </form>
          ) : (
            <div className="success-message">
              <div className="success-icon">✓</div>
              <h3>Thank You!</h3>
              <p>Your Family Digital Well-being Starter Kit is on its way to your inbox.</p>
              <p className="success-note">Check your email (including spam folder) in the next few minutes.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadMagnetForm;
